import { Modal } from '@pa/ui'
import { useId, useRef } from 'react'
import { relTime } from '../model/dates'
import type { SettingsConflict } from '../SettingsDraftProvider'

export const CONFLICT_COPY = {
  title: 'Settings were changed by someone else',
  body: (name: string, ago: string) =>
    `${name} saved changes ${ago}. Reload to see them; your unsaved edits will be lost.`,
  /** No name in `updatedBy` (a deleted user, API.md §1.6 `updatedBy: null`): task 09 assumption. */
  someone: 'Someone else',
} as const

export const LEAVE_COPY = {
  title: 'Discard unsaved changes?',
  body: "You have unsaved settings. If you leave, they'll be lost.",
  stay: 'Keep editing',
  leave: 'Discard and leave',
} as const

/**
 * C-04 "Someone else saved first" (UserFlows C-04, D-34, D-60): **Reload** discards the draft and loads the latest;
 * **Cancel** (or Escape) keeps the draft so it can be copied.
 */
export function ConflictDialog({
  conflict,
  onReload,
  onCancel,
}: {
  conflict: SettingsConflict
  onReload: () => void
  onCancel: () => void
}) {
  const id = useId()
  const ago = conflict.updatedAt ? relTime(conflict.updatedAt) : 'just now'
  return (
    <Modal onClose={onCancel} labelledBy={`${id}-title`} describedBy={`${id}-body`}>
      <div className="modal-head">
        <div>
          <h2 id={`${id}-title`}>{CONFLICT_COPY.title}</h2>
        </div>
      </div>
      <div className="modal-body">
        <p id={`${id}-body`}>{CONFLICT_COPY.body(conflict.name ?? CONFLICT_COPY.someone, ago)}</p>
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={onReload}>
          Reload
        </button>
      </div>
    </Modal>
  )
}

/**
 * The leave prompt (Architecture P-07, Frontend §6.6): non-dismissible; **Keep editing** is the primary action and
 * takes focus.
 */
export function LeavePrompt({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  const id = useId()
  const stay = useRef<HTMLButtonElement>(null)
  return (
    <Modal
      onClose={onStay}
      labelledBy={`${id}-title`}
      describedBy={`${id}-body`}
      dismissible={false}
      initialFocus={stay}
    >
      <div className="modal-head">
        <div>
          <h2 id={`${id}-title`}>{LEAVE_COPY.title}</h2>
        </div>
      </div>
      <div className="modal-body">
        <p id={`${id}-body`}>{LEAVE_COPY.body}</p>
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onLeave}>
          {LEAVE_COPY.leave}
        </button>
        <button ref={stay} type="button" className="btn btn-primary" onClick={onStay}>
          {LEAVE_COPY.stay}
        </button>
      </div>
    </Modal>
  )
}
